import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { Button, Image, Input, message, Space, Upload } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config/api';

// A backend `fileFilter`-e pontosan ezt a négy kiterjesztést engedi át
// (`backend-node-tsc-nestjs/src/upload/upload.controller.ts`). Itt csak a
// fájlválasztót szűrjük vele — a valódi kapu a szerveren van, a böngészőben
// beállított `accept` megkerülhető.
const ACCEPTED_IMAGE_TYPES = '.jpg,.jpeg,.png,.gif,.webp';

// Ensure we have the base URL without the /api suffix for upload actions
const BASE_URL = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;

interface ImageUploadProps {
  value?: string;
  onChange?: (url?: string) => void;
  uploadUrl: string; // '/api/upload/logo' vagy '/api/upload/image'
  fieldName: string; // 'logo' vagy 'image'
  maxSize?: number; // MB-ban
  placeholder?: string;
  /**
   * A fájlválasztó szűrése. A backend `fileFilter`-e ugyanezt a négy
   * kiterjesztést engedi (`upload.controller.ts`), tehát a böngésző már a
   * választásnál ugyanazt a kört kínálja, amit a szerver elfogad.
   */
  accept?: string;
  /**
   * Feltöltés MELLETT külső URL beírása is. Opcionális, mert a meglévő három
   * hívási hely (esemény borítókép, esemény logó, szervezet logó) mindig
   * feltöltéssel dolgozik. Az előadói fotónál és a szponzor-logónál viszont
   * kell: a ma élő szponzorlogók külső oldalra hivatkoznak, és ezt a
   * munkamódot nem veheti el a feltöltés bevezetése.
   */
  allowUrlInput?: boolean;
}

interface ImageUploadRequest {
  uploadUrl: string;
  fieldName: string;
  maxSize: number;
  accept?: string;
  /** A sikeres feltöltés teljes (backend host-os) URL-je. */
  onUploaded: (url: string) => void;
}

/**
 * A feltöltés gomb bekötése (végpont, token, méret- és típusellenőrzés,
 * visszajelzések). Kiemelve, mert a szponzor-logó táblázat (#171) soronként
 * ugyanezt a feltöltést használja, csak nagy előnézet nélkül.
 */
export function useImageUploadProps({
  uploadUrl,
  fieldName,
  maxSize,
  accept = ACCEPTED_IMAGE_TYPES,
  onUploaded,
}: ImageUploadRequest): { uploadProps: UploadProps; loading: boolean } {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleChange: UploadProps['onChange'] = (info) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      return;
    }
    if (info.file.status === 'done') {
      if (info.file.response?.url) {
        onUploaded(`${BASE_URL}${info.file.response.url}`);
        message.success(t('imageUpload.uploaded'));
      }
      setLoading(false);
    }
    if (info.file.status === 'error') {
      console.error('Upload error:', info.file.error, info.file.response);
      message.error(
        t('imageUpload.uploadFailed', {
          reason:
            info.file.error?.message ||
            info.file.response?.message ||
            t('imageUpload.unknownError'),
        })
      );
      setLoading(false);
    }
  };

  const beforeUpload = (file: File) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error(t('imageUpload.onlyImages'));
      return false;
    }
    const isLtSize = file.size / 1024 / 1024 < maxSize;
    if (!isLtSize) {
      message.error(t('imageUpload.tooLarge', { maxSize }));
      return false;
    }
    return true;
  };

  const token = localStorage.getItem('access_token');

  // Construct the upload URL properly
  const uploadAction = uploadUrl.startsWith('/api')
    ? `${BASE_URL}${uploadUrl}`
    : `${BASE_URL}/api${uploadUrl.startsWith('/') ? uploadUrl : `/${uploadUrl}`}`;

  return {
    loading,
    uploadProps: {
      name: fieldName,
      accept,
      action: uploadAction,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      beforeUpload,
      onChange: handleChange,
      showUploadList: false,
    },
  };
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  uploadUrl,
  fieldName,
  maxSize = 5,
  placeholder,
  accept,
  allowUrlInput = false,
}) => {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState<string>(value || '');

  // Update internal state when value prop changes
  React.useEffect(() => {
    setImageUrl(value || '');
  }, [value]);

  const { uploadProps, loading } = useImageUploadProps({
    uploadUrl,
    fieldName,
    maxSize,
    accept,
    onUploaded: (fullUrl) => {
      setImageUrl(fullUrl);
      onChange?.(fullUrl);
    },
  });

  const handleRemove = () => {
    setImageUrl('');
    onChange?.('');
    message.success(t('imageUpload.removed'));
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {imageUrl ? (
        <div style={{ textAlign: 'center' }}>
          <Image
            src={imageUrl}
            alt={t('imageUpload.alt')}
            style={{ maxWidth: 300, maxHeight: 200, borderRadius: 8 }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3PTWBSGcbGzM6GCKqlIBRV0dHRJFarQ0eUT8LH4BnRU0NHR0UEFVdIlFRV7TzRksomPY8uykTk/zewQfKw/9znv4yvJynLv4uLiV2dBoDiBf4qP3/ARuCRABEFAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghgg0Aj8i0JO4OzsrPv69Wv+hi2qPHr0qNvf39+iI7duPjZs2Nji7Z39="
          />
          <div style={{ marginTop: 16 }}>
            <Space>
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />} loading={loading}>
                  {t('imageUpload.replace')}
                </Button>
              </Upload>
              <Button icon={<DeleteOutlined />} danger onClick={handleRemove}>
                {t('imageUpload.delete')}
              </Button>
            </Space>
          </div>
        </div>
      ) : (
        <div
          style={{
            textAlign: 'center',
            padding: 32,
            border: '1px dashed #d9d9d9',
            borderRadius: 8,
          }}
        >
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />} loading={loading} size="large">
              {placeholder ?? t('imageUpload.upload')}
            </Button>
          </Upload>
          <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
            {t('imageUpload.sizeHint', { maxSize })}
          </div>
        </div>
      )}

      {/* A feltöltés nem váltja ki a külső URL-t, hanem kiegészíti — lásd
          `allowUrlInput`. A két beviteli mód ugyanarra az egy értékre ír. */}
      {allowUrlInput && (
        <div>
          <Input
            value={imageUrl}
            placeholder={t('imageUpload.urlPlaceholder')}
            allowClear
            onChange={(e) => {
              const next = e.target.value;
              setImageUrl(next);
              onChange?.(next);
            }}
          />
          <div style={{ marginTop: 4, color: '#666', fontSize: 12 }}>
            {t('imageUpload.urlHint')}
          </div>
        </div>
      )}
    </Space>
  );
};

export default ImageUpload;
